import React, { useState, useEffect, useCallback } from 'react';
import { learnerApi, WrongAnswerRow, WrongHeatmapRow } from '../../api/learner';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Flame } from 'lucide-react';
import { Pagination } from '../../components/shared/Pagination';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { EXAM_KIND_LABELS, labelOf } from '@/lib/labels';
import { EmptyState, ErrorState, FilterChips, FormField, PageHeader, Toolbar, type FilterChip } from '../../components/app';

const PAGE_SIZE = 25;

export default function WrongAnswersPage() {
  const [tab, setTab] = useState<'list' | 'heatmap'>('list');

  // List state
  const [items, setItems] = useState<WrongAnswerRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [questionIdFilter, setQuestionIdFilter] = useState('');
  const [minWrongCount, setMinWrongCount] = useState('');
  // То, что видно в полях; в запрос попадает через 300 мс после ввода.
  const [userIdInput, setUserIdInput] = useState('');
  const [questionIdInput, setQuestionIdInput] = useState('');
  const [minWrongInput, setMinWrongInput] = useState('');
  const [listError, setListError] = useState<string | null>(null);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  // У каждого поля свой таймер, чтобы быстрый ввод в соседнее поле не отменял предыдущее.
  const applyUserId = useDebouncedCallback((v: string) => { setUserIdFilter(v); setPage(1); });
  const applyQuestionId = useDebouncedCallback((v: string) => { setQuestionIdFilter(v); setPage(1); });
  const applyMinWrong = useDebouncedCallback((v: string) => { setMinWrongCount(v); setPage(1); });

  // Heatmap state
  const [heatmap, setHeatmap] = useState<WrongHeatmapRow[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const params: any = { page, pageSize: PAGE_SIZE };
      if (userIdFilter.trim()) params.userId = userIdFilter.trim();
      if (questionIdFilter.trim()) params.questionId = Number(questionIdFilter.trim());
      if (minWrongCount.trim()) params.minWrongCount = Number(minWrongCount.trim());
      const res = await learnerApi.listWrongAnswers(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setListError(getErrorMessage(e, 'Не удалось загрузить ошибки'));
    } finally {
      setListLoading(false);
    }
  }, [page, userIdFilter, questionIdFilter, minWrongCount]);

  const fetchHeatmap = useCallback(async () => {
    setHeatmapLoading(true);
    setHeatmapError(null);
    try {
      const res = await learnerApi.wrongHeatmap(100);
      setHeatmap(res.items);
    } catch (e: any) {
      setHeatmapError(getErrorMessage(e, 'Не удалось загрузить тепловую карту'));
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'list') fetchList();
    else fetchHeatmap();
  }, [tab, fetchList, fetchHeatmap]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('ru-RU', {
      timeZone: 'Asia/Tashkent',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const maxHeatmapWrong = heatmap.length > 0 ? Math.max(...heatmap.map((r) => r.total_wrong)) : 1;
  const nf = new Intl.NumberFormat('ru-RU');

  const chips: FilterChip[] = [];
  if (userIdFilter.trim())
    chips.push({
      key: 'user',
      label: `Пользователь: ${userIdFilter.trim().slice(0, 8)}…`,
      onRemove: () => { setUserIdInput(''); setUserIdFilter(''); setPage(1); },
    });
  if (questionIdFilter.trim())
    chips.push({
      key: 'question',
      label: `Вопрос #${questionIdFilter.trim()}`,
      onRemove: () => { setQuestionIdInput(''); setQuestionIdFilter(''); setPage(1); },
    });
  if (minWrongCount.trim())
    chips.push({
      key: 'min',
      label: `Ошибок от ${minWrongCount.trim()}`,
      onRemove: () => { setMinWrongInput(''); setMinWrongCount(''); setPage(1); },
    });
  const resetAll = () => {
    setUserIdInput('');
    setQuestionIdInput('');
    setMinWrongInput('');
    setUserIdFilter('');
    setQuestionIdFilter('');
    setMinWrongCount('');
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Лог ошибок"
        subtitle="Где ученики ошибаются: лог по пользователям и тепловая карта самых сложных вопросов"
        actions={
          <Tabs value={tab} onValueChange={(v) => setTab(v as 'list' | 'heatmap')}>
            <TabsList aria-label="Вид">
              <TabsTrigger value="list">По пользователям</TabsTrigger>
              <TabsTrigger value="heatmap">Тепловая карта вопросов</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      {tab === 'list' && (
        <>
          <Card className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
            <FormField label="ID пользователя" tone="filter">
              <Input
                placeholder="UUID или его начало"
                className="font-mono"
                value={userIdInput}
                onChange={(e) => { setUserIdInput(e.target.value); applyUserId(e.target.value); }}
              />
            </FormField>
            <FormField label="ID вопроса" tone="filter">
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Например, 512"
                value={questionIdInput}
                onChange={(e) => { setQuestionIdInput(e.target.value); applyQuestionId(e.target.value); }}
              />
            </FormField>
            <FormField label="Мин. число ошибок" tone="filter">
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Например, 3"
                value={minWrongInput}
                onChange={(e) => { setMinWrongInput(e.target.value); applyMinWrong(e.target.value); }}
              />
            </FormField>
          </Card>

          <Card className="overflow-hidden">
            {listError ? (
              <ErrorState title="Не удалось загрузить ошибки" message={listError} onRetry={fetchList} />
            ) : (
              <>
                <Toolbar title={listLoading ? 'Ошибки' : `Найдено ${nf.format(total)}`}>
                  <FilterChips chips={chips} onReset={resetAll} />
                </Toolbar>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Пользователь</TableHead>
                      <TableHead>Вопрос</TableHead>
                      <TableHead className="text-right">Ошибок</TableHead>
                      <TableHead>Последняя ошибка</TableHead>
                      <TableHead>Контекст</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listLoading ? (
                      Array.from({ length: 8 }, (_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 5 }, (__, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-full max-w-28" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : items.length === 0 ? (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={5}>
                          <EmptyState
                            icon={<Flame />}
                            title="Нет записей об ошибках по заданным фильтрам"
                            action={chips.length > 0 ? <Button variant="secondary" onClick={resetAll}>Сбросить фильтры</Button> : undefined}
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((it) => (
                        <TableRow key={it.id}>
                          <TableCell className="font-mono text-xs text-text-2" title={it.user_id}>
                            {it.user_id.slice(0, 8)}…
                          </TableCell>
                          <TableCell className="font-mono">#{it.question_id}</TableCell>
                          <TableCell className="text-right font-bold text-neg tabular-nums">{it.wrong_count}</TableCell>
                          <TableCell className="whitespace-nowrap tabular-nums">{formatDate(it.last_wrong_at)}</TableCell>
                          <TableCell>{labelOf(EXAM_KIND_LABELS, it.last_session_kind)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                {totalPages > 1 && (
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    total={total}
                    itemsPerPage={PAGE_SIZE}
                    onPageChange={setPage}
                  />
                )}
              </>
            )}
          </Card>
        </>
      )}

      {tab === 'heatmap' && (
        <Card className="overflow-hidden">
          <Toolbar title="100 самых сложных вопросов" end="по всем ученикам" />
          <div className="px-[18px] pb-[18px]">
            {heatmapLoading ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} className="h-11 w-full rounded-xl" />
                ))}
              </div>
            ) : heatmapError ? (
              <ErrorState title="Не удалось загрузить тепловую карту" message={heatmapError} onRetry={fetchHeatmap} />
            ) : heatmap.length === 0 ? (
              <EmptyState icon={<Flame />} title="Пока нет данных" />
            ) : (
              <ol aria-label="Самые сложные вопросы" className="m-0 flex list-none flex-col gap-2 p-0">
                {heatmap.map((row) => {
                  const ratio = row.total_wrong / maxHeatmapWrong;
                  return (
                    <li key={row.question_id} className="relative overflow-hidden rounded-xl bg-surface-alt px-4 py-2.5">
                      <div
                        aria-hidden
                        className="absolute inset-y-0 left-0 bg-neg-soft"
                        style={{ width: `${Math.max(ratio * 100, 3)}%` }}
                      />
                      <div className="relative flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                        <span className="font-mono font-semibold">Вопрос #{row.question_id}</span>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="flex items-center gap-1.5">
                            <span className="text-text-2">Всего ошибок</span>
                            <b className="text-neg tabular-nums">{nf.format(row.total_wrong)}</b>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="text-text-2">Пользователей</span>
                            <b className="tabular-nums">{nf.format(row.users_affected)}</b>
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
